import { api } from '../../lib/api';
import type { FlowError } from '../../lib/surveyBranching';
import type { AudiencePreviewResult, AudienceScope } from '../../types/broadcast';
import type { Survey, SurveyDraftInput } from '../../types/survey';
import type { SurveyResultsDto } from '../../types/surveyResults';

// Client API for the surveys module (Phase 6B). POST/PATCH return the survey
// plus non-blocking flowWarnings (NEW-1 — validateSurveyFlow output the editor
// surfaces inline). The compose page holds the whole draft and saves it
// wholesale, so update is a full replace of the mutable subset (mirrors
// broadcasts).

export type SurveyWithWarnings = { survey: Survey; flowWarnings: FlowError[] };

export async function listSurveys(): Promise<Survey[]> {
  const res = await api<{ surveys: Survey[] }>('/api/surveys');
  return res.surveys;
}

export async function getSurvey(id: string): Promise<SurveyWithWarnings> {
  return api<SurveyWithWarnings>(`/api/surveys/${id}`);
}

export async function getSurveyResults(id: string): Promise<SurveyResultsDto> {
  return api<SurveyResultsDto>(`/api/surveys/${id}/results`);
}

export async function createSurvey(input: SurveyDraftInput): Promise<SurveyWithWarnings> {
  return api<SurveyWithWarnings>('/api/surveys', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export async function updateSurvey(
  id: string,
  input: SurveyDraftInput,
): Promise<SurveyWithWarnings> {
  return api<SurveyWithWarnings>(`/api/surveys/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(input),
  });
}

export async function deleteSurvey(id: string): Promise<{ kind: 'hard' | 'soft' }> {
  return api<{ kind: 'hard' | 'soft' }>(`/api/surveys/${id}`, { method: 'DELETE' });
}

export async function listDeletedSurveys(): Promise<Survey[]> {
  const res = await api<{ surveys: Survey[] }>('/api/surveys/deleted');
  return res.surveys;
}

export async function restoreSurvey(id: string): Promise<Survey> {
  const res = await api<{ survey: Survey }>(`/api/surveys/${id}/restore`, { method: 'POST' });
  return res.survey;
}

export async function duplicateSurvey(id: string): Promise<Survey> {
  const res = await api<{ survey: Survey }>(`/api/surveys/${id}/duplicate`, { method: 'POST' });
  return res.survey;
}

export async function closeSurvey(id: string): Promise<Survey> {
  const res = await api<{ survey: Survey }>(`/api/surveys/${id}/close`, { method: 'POST' });
  return res.survey;
}

export async function cancelSurvey(id: string): Promise<Survey> {
  const res = await api<{ survey: Survey }>(`/api/surveys/${id}/cancel`, { method: 'POST' });
  return res.survey;
}

export type ResendInput = {
  channels?: ('email' | 'sms')[];
  customMessage?: { emailSubject?: string; emailBodyMarkdown?: string; smsBody?: string };
};

export async function resendNonResponders(id: string, input: ResendInput): Promise<{ reminded: number }> {
  return api<{ reminded: number }>(`/api/surveys/${id}/resend-non-responders`, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

// Freeze + dispatch invites. Survey goes draft → sending → open. 400 on zero
// reachable recipients (F6) or an unresolved flow; 409 if not a draft.
export async function sendSurvey(id: string): Promise<{ survey: Survey; dispatched: number }> {
  return api<{ survey: Survey; dispatched: number }>(`/api/surveys/${id}/send`, { method: 'POST' });
}

// Returns the SAME AudiencePreviewResult shape as the broadcast preview so the
// shared RecipientPreviewModal reuses it. details=true returns the full
// recipient list (modal); default is counts + a capped sample (inline summary).
export async function previewSurveyAudience(
  scope: AudienceScope,
  details = false,
): Promise<AudiencePreviewResult> {
  return api<AudiencePreviewResult>(
    `/api/surveys/preview-audience${details ? '?details=true' : ''}`,
    { method: 'POST', body: JSON.stringify(scope) },
  );
}

// ---------- draft helpers (shared by compose) ----------

export function emptyAudienceScope(): AudienceScope {
  return {
    brandSelection: { brandAccountIds: [] },
    selectedAccountIds: [],
    excludedAccountIds: [],
    contactLevelFilter: [],
    commsPurposeFilter: [],
    accountManagerFilter: [],
  };
}

// A pristine draft for /surveys/new. Sensible defaults: per_recipient link mode,
// email channel, an empty intro/outro with default CTA labels.
export function emptySurveyDraft(): SurveyDraftInput {
  return {
    title: '',
    description: null,
    introScreen: { title: '', bodyMarkdown: '', ctaLabel: 'Get started', estimatedDurationMinutes: null },
    steps: [],
    outroScreen: { title: 'Thanks for your response', bodyMarkdown: '', ctaLabel: null, ctaUrl: null },
    linkMode: 'per_recipient',
    closeDate: null,
    audienceScope: emptyAudienceScope(),
    channels: ['email'],
    emailSubject: '',
    emailBodyMarkdown: '',
    smsBody: '',
    emailFromMode: 'account_manager',
    emailFromCustomName: null,
    emailReplyToMode: 'account_manager',
    emailReplyToCustomEmail: null,
  };
}

// Project a loaded Survey down to the editable draft subset (edit mode seed).
export function surveyToDraft(s: Survey): SurveyDraftInput {
  return {
    title: s.title,
    description: s.description,
    introScreen: s.introScreen,
    steps: s.steps,
    outroScreen: s.outroScreen,
    linkMode: s.linkMode,
    closeDate: s.closeDate,
    audienceScope: s.audienceScope,
    channels: s.channels,
    emailSubject: s.emailSubject,
    emailBodyMarkdown: s.emailBodyMarkdown,
    smsBody: s.smsBody,
    emailFromMode: s.emailFromMode,
    emailFromCustomName: s.emailFromCustomName,
    emailReplyToMode: s.emailReplyToMode,
    emailReplyToCustomEmail: s.emailReplyToCustomEmail,
  };
}
